import { type ReactNode } from "react";
import { cn } from "../../lib/utils";

type BadgePosition = "tr" | "br" | "tl";

const positionClasses: Record<BadgePosition, string> = {
  tr: "top-0 right-0 -translate-y-1/4",
  br: "bottom-0 right-0 translate-x-1/4 translate-y-1/4",
  tl: "top-0 left-0 -translate-x-1/8 -translate-y-1/4",
};

type BadgeProps = {
  children: ReactNode;
  /** Whether the badge is visible */
  show?: boolean;
  /** Badge placement relative to children */
  position?: BadgePosition;
  /** When provided, renders a text pill instead of a dot */
  label?: string;
  /** Extra classes merged onto the badge element */
  className?: string;
  /** When true, the wrapper is block-level and stretches to its parent's width (default: inline). */
  block?: boolean;
};

function Badge({
  children,
  show = false,
  position = "tr",
  label,
  className,
  block = false,
}: BadgeProps): React.JSX.Element {
  const hasLabel = label !== undefined && label.length > 0;
  return (
    <span className={cn("relative", block ? "flex w-full" : "inline-flex")}>
      {children}
      {show &&
        (hasLabel ? (
          <span
            className={cn(
              "absolute inline-flex items-center justify-center rounded-full bg-ds-strawberry-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white",
              positionClasses[position],
              className
            )}
          >
            <span className="absolute inset-0 animate-ping md:animate-pulse scale-80 rounded-full bg-ds-strawberry-400 opacity-75" />
            <span className="relative">{label}</span>
          </span>
        ) : (
          <span
            className={cn("absolute flex size-2", positionClasses[position])}
          >
            <span className="absolute inline-flex size-full animate-ping md:animate-pulse rounded-full bg-ds-strawberry-400 opacity-75" />
            <span
              className={cn(
                "relative inline-flex size-full rounded-full bg-ds-strawberry-500",
                className
              )}
            />
          </span>
        ))}
    </span>
  );
}

export { Badge, type BadgeProps };
